import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Info, Loader2, Navigation } from 'lucide-react'

import { Field } from '../../components/Field'
import { useRoadbook } from '../../data/roadbookContext'
import { travelDay } from '../../lib/calendar'
import { dayLabel, today } from '../../lib/format'
import {
  NAV_ARTEN,
  NAV_GLIEDERUNG_VORGABE,
  NAV_GLIEDERUNGEN,
  NAV_ORDNER_NACH_ART,
  navArtName,
  navDateiname,
  navGliederungName,
  navMeldungen,
  navUmfang,
  packeKmz,
  sammleNavExport,
  schreibeGpx,
  schreibeKml,
  type KmzFoto,
  type NavArt,
  type NavGliederung,
} from '../../lib/navexport'

/**
 * Die Reise für ein Navigationsgerät ausgeben.
 *
 * Der Bereich steht neben dem JSON-Export, weil beides dasselbe ist: den
 * Bestand aus der App herausholen. Er ist trotzdem ein eigener Block, denn er
 * ist etwas anderes - die JSON-Datei ist das Sicherungsband und kommt hier
 * wieder herein, diese Dateien gehen in fremde Programme und nie zurück.
 *
 * Alles Rechnen steht in lib/navexport.ts. Hier stehen nur die Auswahl, die
 * Vorschau und die drei Knöpfe.
 *
 * Zwei Dinge macht dieser Bereich ausdrücklich:
 *
 * Er zeigt vorher, was in der Datei landet - nach Art gezählt, nicht als
 * Versprechen. Wer sechzehn Etappen exportiert, soll die 16 Ziele und die 34
 * Campingplätze gesehen haben, bevor er auf dem Telefon danach sucht.
 *
 * Und er warnt vor den Grenzen von Google My Maps, bevor sie zuschlagen.
 * Deren Import bricht stillschweigend ab: er meldet Erfolg, und die Hälfte
 * fehlt. Das ist genau die Sorte Fehler, die in dieser App sonst nirgends
 * stehen bleibt. Zu jeder Warnung steht dabei, was hilft - die Gliederung
 * umstellen oder je Etappe exportieren -, nachgerechnet und nicht geraten.
 */
export function NavExport() {
  const { trip, photosByStage, loadPhoto } = useRoadbook()

  /** Leer heisst: die ganze Reise. */
  const [stageId, setStageId] = useState('')
  const [gliederung, setGliederung] = useState<NavGliederung>(NAV_GLIEDERUNG_VORGABE)
  const [laeuft, setLaeuft] = useState(false)
  const [packt, setPackt] = useState<{ fertig: number; gesamt: number } | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const sichtbar = useRef(true)
  useEffect(() => {
    sichtbar.current = true
    return () => {
      sichtbar.current = false
    }
  }, [])

  const nav = useMemo(
    () => sammleNavExport(trip, { ...(stageId ? { stageId } : {}), fotos: photosByStage }),
    [trip, stageId, photosByStage],
  )
  const umfang = useMemo(() => navUmfang(nav, gliederung), [nav, gliederung])

  // Die KML wird für die Vorschau wirklich gebaut und nicht geschätzt: die
  // Grenze von Google liegt bei 5 MB, und neben einer harten Grenze ist eine
  // geschätzte Zahl keine Auskunft. Der Zeitstempel darin spielt für die
  // Grösse keine Rolle - er ist immer gleich lang -, deshalb steht in der
  // Vorschau ein fester und in der Datei der des Knopfdrucks. Gebaut wird die
  // gewählte Gliederung: die Ordner wiegen zwar wenig, aber "rund" heisst hier
  // nicht "ungefähr die andere Datei".
  const kmlBytes = useMemo(
    () => new TextEncoder().encode(schreibeKml(nav, 0, true, gliederung)).length,
    [nav, gliederung],
  )
  const meldungen = useMemo(() => navMeldungen(umfang, kmlBytes), [umfang, kmlBytes])

  const laden = useCallback(
    (blob: Blob, endung: string) => {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = navDateiname(nav.titel, endung, today())
      link.click()
      URL.revokeObjectURL(url)
    },
    [nav.titel],
  )

  function gpx() {
    setMeldung(null)
    setFehler(null)
    const datei = schreibeGpx(nav, Date.now())
    laden(new Blob([datei], { type: 'application/gpx+xml' }), 'gpx')
    // Routenpunkte sind die angefahrenen: Etappenziele und Zwischenpunkte.
    // Alles andere wird angezeigt und vorgelesen, aber nicht angefahren.
    const routenpunkte = umfang.proArt.ziel + umfang.proArt.zwischen
    setMeldung(
      `GPX erstellt: ${zahl(routenpunkte, 'Routenpunkt', 'Routenpunkte')}, ` +
        `${zahl(umfang.punkte - routenpunkte, 'Wegpunkt', 'Wegpunkte')}, ` +
        `${zahl(umfang.linien, 'Strecke', 'Strecken')}.`,
    )
  }

  function kml() {
    setMeldung(null)
    setFehler(null)
    // Ohne Fotos: eine einzelne KML-Datei hat keinen Ort, an dem Bilder
    // liegen könnten, und ein Verweis ins Leere ist in Google Earth ein
    // Kasten mit einem Kreuz darin.
    const datei = schreibeKml(nav, Date.now(), false, gliederung)
    laden(new Blob([datei], { type: 'application/vnd.google-earth.kml+xml' }), 'kml')
    setMeldung(
      `KML erstellt: ${zahl(umfang.objekte, 'Objekt', 'Objekte')} in ` +
        `${zahl(umfang.ebenen, 'Ebene', 'Ebenen')}, gegliedert ${navGliederungName(gliederung)}.`,
    )
  }

  async function kmz() {
    setLaeuft(true)
    setMeldung(null)
    setFehler(null)
    setPackt({ fertig: 0, gesamt: umfang.fotos })
    try {
      const bilder: KmzFoto[] = []
      let fehlend = 0
      let fertig = 0

      for (const etappe of nav.etappen) {
        for (const foto of etappe.ziel.fotos) {
          const blob = await loadPhoto(foto.id, etappe.id)
          if (blob)
            bilder.push({ pfad: foto.pfad, bytes: new Uint8Array(await blob.arrayBuffer()) })
          else fehlend++
          fertig++
          if (sichtbar.current) setPackt({ fertig, gesamt: umfang.fotos })
        }
      }

      // Die Beschreibungen verweisen nur auf Bilder, die auch im Archiv
      // liegen: ein Verweis auf ein Foto, das nicht geladen werden konnte,
      // wäre ein Kasten mit einem Kreuz.
      const geladen = new Set(bilder.map((b) => b.pfad))
      const bereinigt = {
        ...nav,
        etappen: nav.etappen.map((e) => ({
          ...e,
          ziel: { ...e.ziel, fotos: e.ziel.fotos.filter((f) => geladen.has(f.pfad)) },
        })),
      }

      const jetzt = Date.now()
      const archiv = packeKmz(schreibeKml(bereinigt, jetzt, true, gliederung), bilder, jetzt)
      laden(new Blob([archiv], { type: 'application/vnd.google-earth.kmz' }), 'kmz')
      if (sichtbar.current) {
        setMeldung(
          `KMZ erstellt: ${zahl(umfang.objekte, 'Objekt', 'Objekte')}, ` +
            `${zahl(bilder.length, 'Foto', 'Fotos')}${fehlend > 0 ? `, ${String(fehlend)} nicht lesbar` : ''}.`,
        )
      }
    } catch (err) {
      console.error('KMZ fehlgeschlagen', err)
      if (sichtbar.current) setFehler('Die KMZ-Datei liess sich nicht bauen.')
    } finally {
      if (sichtbar.current) {
        setPackt(null)
        setLaeuft(false)
      }
    }
  }

  // Ohne Etappen gibt es nichts zu exportieren. Die Knöpfe standen hier
  // trotzdem: die GPX-Datei kam mit einem Kopf und keinem einzigen Punkt
  // heraus, die KML mit Stilen und null Ordnern, und darunter meldete die App
  // "GPX erstellt: 0 Routenpunkte, 0 Wegpunkte, 0 Strecken" - ein Download,
  // der nach Erfolg aussieht und nichts enthält.
  if (trip.stages.length === 0) {
    return (
      <div className="block">
        <h4>
          <Navigation size={13} /> Für das Navigationsgerät
        </h4>
        <p className="leer">
          Ohne Etappen gibt es nichts zu exportieren. Sobald die erste Etappe steht, entstehen hier
          GPX für das Navigationsgerät und KML für Google Earth.
        </p>
      </div>
    )
  }

  return (
    <div className="block">
      <h4>
        <Navigation size={13} /> Für das Navigationsgerät
      </h4>
      <p>
        GPX ist das Navigationsformat: die Etappenziele und die Zwischenpunkte werden zu
        Routenpunkten und damit angefahren — daran hängt, dass das Gerät den Pass nimmt und nicht
        den Tunnel. Alles andere wird zu Wegpunkten, die angezeigt und von OsmAnd unterwegs
        vorgelesen werden. KML ist das Anzeigeformat für Google My Maps und Google Earth: dort
        stehen Preise und Notizen als Tabelle, und in der KMZ reisen die Fotos mit. Gebaut wird
        beides hier auf dem Gerät, also auch ohne Netz.
      </p>

      <div className="raster-2">
        <Field label="Umfang">
          <select value={stageId} onChange={(e) => setStageId(e.target.value)}>
            <option value="">Ganze Reise</option>
            {/*
              Diese Liste zeigt auch die ausgelassenen Etappen - man soll
              sehen, dass es sie gibt und warum sie in keiner Datei stehen. Ein
              Reisetag steht ihnen aber nicht zu: sie kosten keinen. Also steht
              vorn entweder der Tag oder das Wort dafür, dass es keinen gibt.
            */}
            {trip.stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.skip ? 'ausgelassen' : `Tag ${dayLabel(travelDay(trip.stages, s))}`} · {s.from} →{' '}
                {s.to}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Ordner der KML">
          <select
            value={gliederung}
            onChange={(e) => setGliederung(e.target.value as NavGliederung)}
          >
            {NAV_GLIEDERUNGEN.map((g) => (
              <option key={g} value={g}>
                {GLIEDERUNG_LABEL[g]}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <p className="abschluss">{GLIEDERUNG_ERKLAERT[gliederung]}</p>

      <ul className="umfang">
        {NAV_ARTEN.map((art) => (
          <li key={art}>
            <b>{umfang.proArt[art]}</b> {navArtName(art)}
            <small>{ARTEN_ERKLAERT[art]}</small>
          </li>
        ))}
        <li>
          <b>{umfang.linien}</b> Strecken
          <small>GPX: Track · KML: Linie</small>
        </li>
        <li>
          <b>{umfang.fotos}</b> Fotos
          <small>nur in der KMZ · {mb(umfang.fotoBytes)}</small>
        </li>
        <li>
          <b>{umfang.objekte}</b> Objekte in {umfang.ebenen} Ebenen
          <small>KML-Datei rund {mb(kmlBytes)}</small>
        </li>
      </ul>

      {meldungen.warnungen.map((text) => (
        <p key={text} className="hinweis warnung">
          <AlertTriangle size={13} />
          <span>{text}</span>
        </p>
      ))}
      {meldungen.hinweise.map((text) => (
        <p key={text} className="hinweis">
          <Info size={13} />
          <span>{text}</span>
        </p>
      ))}

      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={gpx} disabled={laeuft}>
          GPX für die Navigation
        </button>
        <button type="button" className="knopf still" onClick={kml} disabled={laeuft}>
          KML ohne Fotos
        </button>
        <button
          type="button"
          className="knopf still"
          onClick={() => void kmz()}
          disabled={laeuft || umfang.fotos === 0}
        >
          {packt ? (
            <>
              <Loader2 size={14} className="dreht" /> Foto {packt.fertig} von {packt.gesamt} …
            </>
          ) : (
            <>KMZ mit {umfang.fotos === 0 ? 'Fotos' : zahl(umfang.fotos, 'Foto', 'Fotos')}</>
          )}
        </button>
      </div>

      {meldung && <p className="hinweis">{meldung}</p>}
      {fehler && <p className="hinweis warnung">{fehler}</p>}

      <p className="abschluss">
        Die GPX-Datei trägt die Erweiterungen von OsmAnd und Garmin nebeneinander: Farbe, Symbol und
        Gruppe je Punkt, dazu ein Warnradius - zwei Kilometer an der Grenze, dreihundert Meter an
        einer Roadbook-Zeile. Geräte, die eine davon nicht kennen, überlesen sie und zeigen das
        neutrale Symbol.
      </p>
    </div>
  )
}

/** Die Gliederungen der KML, wie sie in der Auswahl stehen. */
const GLIEDERUNG_LABEL: Record<NavGliederung, string> = {
  art: 'Nach Art · ein Ordner je Art',
  etappe: 'Nach Etappe · einer je Tag',
}

/**
 * Wofür die eine und wofür die andere.
 *
 * Der Satz nennt die Grenze und nicht den Geschmack: wer nach Etappe gliedert,
 * soll wissen, dass Google My Maps ab dem elften Tag aufhört - und wer nach Art
 * gliedert, dass er dafür nicht mehr einzelne Tage schalten kann.
 */
const GLIEDERUNG_ERKLAERT: Record<NavGliederung, string> = {
  art:
    `${NAV_ORDNER_NACH_ART.join(', ')} - je in einem Ordner. Höchstens ` +
    `${String(NAV_ORDNER_NACH_ART.length)} Ebenen, und Google My Maps nimmt zehn je Karte: so ` +
    'passt jede Reise hinein. Jeder Punkt trägt seinen Tag im Namen; einzelne Tage ein- und ' +
    'ausblenden geht dafür nicht.',
  etappe:
    'Ein Ordner je Tag, zum Ein- und Ausblenden einzelner Etappen. Google Earth und OsmAnd kennen dafür keine Grenze; Google My Maps nimmt zehn Ebenen je Karte und lässt alles darüber weg.',
}

/** Was aus einer Art in den beiden Formaten wird. */
const ARTEN_ERKLAERT: Record<NavArt, string> = {
  ziel: 'GPX: Routenpunkt · KML: Ortsmarke',
  zwischen: 'erzwingt den Weg: GPX-Routenpunkt vor dem Ziel',
  camping: 'an der eingetragenen Lage, sonst am Etappenziel',
  grenze: 'aus den Grenz-Tulpen, grosser Warnradius',
  faehre: 'aus den Fähr-Tulpen, Warnung 2 km vorher',
  tulpe: 'aus dem Kilometerstand auf der Strecke',
  hinweis: 'am Start der Etappe, nur sichtbar',
}

function zahl(wert: number, einzahl: string, mehrzahl: string): string {
  return `${String(wert)} ${wert === 1 ? einzahl : mehrzahl}`
}

function mb(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} kB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
